<script setup>
import { computed, ref, watch, nextTick } from 'vue'
import { useStore } from 'vuex'
import { track, EVENTS } from '@/lib/tracking'
import ModalBox from '@/components/modals/ModalBox.vue'
import FormButton from '@/components/Atoms/FormButton.vue'
import FormSwitch from '@/components/Atoms/FormSwitch.vue'
import Alert from '@/components/Atoms/Alert.vue'
import ProjectForm from './ProjectForm.vue'

const props = defineProps({
  modelValue: {
    type: Boolean,
    default: false,
  },
})

/**
 * `created` carries the new workspace and whether the user asked to be taken
 * into it. The parent owns the switch so the sidebar's switching state and
 * overlay stay the single source of truth.
 */
const emit = defineEmits(['update:modelValue', 'created'])

const store = useStore()

const isOpen = computed({
  get: () => props.modelValue,
  set: (value) => emit('update:modelValue', value),
})

const form = ref(null)
const draft = ref({ name: '', color: '' })
const openAfterCreate = ref(true)
const submitting = ref(false)
const errorMessage = ref('')

const allowance = computed(() => store.getters['Projects/projectAllowance'])
const hasLimit = computed(() => Number(allowance.value.limit) > 0)
const remaining = computed(() => Math.max(Number(allowance.value.limit) - Number(allowance.value.used), 0))

const canSubmit = computed(() => !!draft.value.name.trim() && !!draft.value.color && !submitting.value)

watch(isOpen, async (open) => {
  if (!open) return
  errorMessage.value = ''
  openAfterCreate.value = true
  await nextTick()
  draft.value = { name: '', color: form.value?.firstFreeColor() || '' }
  form.value?.focus()
})

const close = () => {
  isOpen.value = false
}

const submit = async () => {
  if (!canSubmit.value) return
  submitting.value = true
  errorMessage.value = ''

  try {
    const project = await store.dispatch('Projects/createProject', {
      name: draft.value.name.trim(),
      color: draft.value.color,
    })
    track(EVENTS.PROJECT_CREATED, {
      project_count: store.getters['Projects/projects'].length,
      open_after_create: openAfterCreate.value,
    })
    isOpen.value = false
    emit('created', project, { open: openAfterCreate.value })
  } catch (error) {
    errorMessage.value = error?.message || 'Could not create the workspace. Please try again.'
  } finally {
    submitting.value = false
  }
}
</script>

<template>
  <ModalBox v-model="isOpen" title="New workspace" size="sm" scroll-body>
    <div class="px-4 sm:px-6 pb-2">
      <p class="text-tiny text-surface-8 mb-5">
        A workspace keeps its own streams, videos, playlists and destinations. Your team is shared,
        and you choose who can access each workspace. Storage, bandwidth and seats come from your
        plan and are shared across every workspace.
      </p>

      <ProjectForm
        ref="form"
        v-model="draft"
        :disabled="submitting"
        :is-error="!!errorMessage"
        @submit="submit"
      />

      <div class="mt-5 flex items-start gap-3 rounded-lg bg-surface-1 px-3 py-2.5">
        <FormSwitch id="project-open-after-create" v-model="openAfterCreate" size="sm" class="mt-0.5" />
        <div class="min-w-0">
          <label for="project-open-after-create" class="block text-tiny text-white cursor-pointer mb-0">Open it after creating</label>
          <p class="text-[11px] text-surface-6 mt-0.5 mb-0">
            Turn this off to stay here — handy when setting up several workspaces in a row.
          </p>
        </div>
      </div>

      <Alert v-if="errorMessage" type="error" class="mt-4" :text="errorMessage" />
    </div>

    <template #footer>
      <div class="flex items-center justify-between gap-3 px-4 sm:px-6 py-4 border-t border-surface-3">
        <p v-if="hasLimit" class="text-[11px] text-surface-6 mb-0 min-w-0 truncate" :title="allowance.planName ? `Your ${allowance.planName} plan includes ${allowance.limit} workspaces` : undefined">
          {{ allowance.used }} of {{ allowance.limit }} workspaces used<span v-if="remaining === 1" class="hidden sm:inline"> · last one on your plan</span>
        </p>
        <span v-else />
        <div class="flex items-center gap-2 shrink-0">
          <FormButton type="secondary" size="md" label="Cancel" @click="close" />
          <FormButton
            type="main"
            size="md"
            label="Create workspace"
            :disabled="!canSubmit"
            :is-loading="submitting"
            @click="submit"
          />
        </div>
      </div>
    </template>
  </ModalBox>
</template>
